import { Component } from 'react'
import { Link } from 'react-router-dom'
import { ErrorMessage, emptyField, errorsLength, formatErrors } from '../../../../insfrastructure/utils/form-validators'
import { RouterProps, withRouter } from '../../../../insfrastructure/utils/with-router-component'
import { login, TokenParams } from '../../../../insfrastructure/services/auth'
import { handleResendVerificationEmail } from '../../../../insfrastructure/utils/email-verification-helpers'
import { handleEmailNotValidatedError } from '../../../../insfrastructure/utils/email-not-validated-handler'
import { setTokensAfterLogin } from '../../../../insfrastructure/services/token-manager'
import { parseJwt } from '../../../../insfrastructure/utils/JWTParser'
import { AUTH_CONSTANTS } from '../../../../insfrastructure/utils/auth-constants'
import ResponseModal from '../../../components/response-modal'
import Toast from '../../../components/toast'
import { SubmitBtn } from '../../../components/submit'
import { Input } from '../../../components/input'
import loginImg from '../../../../assets/images/login.png'
import logoImg from '../../../../assets/images/logo-white.png'
import { analytics } from '../../../../insfrastructure/services/analytics'
import './styles.sass'

interface LoginForm {
    username: string;
    password: string;
    remember_me: boolean;
}

interface ResponseData {
    type: 'success' | 'error';
    title: string;
    text: () => JSX.Element;
}

interface LoginState {
    form: LoginForm;
    formErrors: Record<string, string[]>;
    loading: boolean;
    sended: boolean;
    responseData: ResponseData | null;
    showOffersToast: boolean;
    resendingEmail: boolean;
}

class Login extends Component<RouterProps, LoginState> {
    state: LoginState = {
        form: {
            username: '',
            password:  '',
            remember_me: false
        },
        formErrors: {},
        loading: false,
        sended: false,
        responseData: null,
        showOffersToast: false,
        resendingEmail: false
    }

    componentDidMount() {
        // Verificar se o usuário foi redirecionado da rota /offers
        const redirectFromOffers = sessionStorage.getItem(AUTH_CONSTANTS.SESSION_STORAGE.REDIRECT_FROM_OFFERS);
        if (redirectFromOffers === 'true') {
            // Exibir toast
            setTimeout(() => {
                this.setState({ showOffersToast: true });
            }, 500);
        }

        // Carregar estado do "Mantenha-me conectado" se existir
        const rememberMe = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.REMEMBER_ME) === 'true';
        if (rememberMe) {
            this.setState({ 
                form: { 
                    ...this.state.form, 
                    remember_me: true 
                } 
            });
        }
    }

    handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            this.onSubmit();
        }
    }

    handleResendVerificationEmail = async () => {
        // Prevenir múltiplos cliques
        if (this.state.resendingEmail) {
            return;
        }

        await handleResendVerificationEmail({
            email: this.state.form.username,
            setResendingEmail: (value) => this.setState({ resendingEmail: value }),
            setResponseData: (data) => this.setState({ responseData: data }),
            invalidEmailMessage: 'Por favor, informe um email válido no campo de login.'
        });
    }

    onSubmit = async () => {
        if(this.state.loading) return;

        const errors = formatErrors({
            username: [emptyField(this.state.form.username, 'string')],
            password: [emptyField(this.state.form.password, 'string')]
        });

        this.setState({ formErrors: errors }, () => {
            if (errorsLength(errors) > 0) {
                const firstErrorElement = document.querySelector('.errors .error') as HTMLElement | null;
                if (firstErrorElement && typeof firstErrorElement.scrollIntoView === 'function') {
                    firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        });

        if(errorsLength(errors) > 0) return;

        this.setState({ loading: true });
        
        // Rastrear tentativa de login
        analytics.trackFormSubmission('login');

        try {
            const response = await login(this.state.form);
            
            analytics.addBreadcrumb('Parsing JWT token', 'auth', 'info');
            const parsedJWT: TokenParams = parseJwt(response.data.access);

            analytics.addBreadcrumb('Storing JWT tokens and user IDs in localStorage', 'auth', 'info');
            setTokensAfterLogin(response.data.access, response.data.refresh, this.state.form.remember_me);
            localStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.USER_ID, parsedJWT.user_id+'');
            localStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.LEGACY_PASSWORD_LOGIN, parsedJWT.legacy_password_login+'');
            
            let route = '';
            if (parsedJWT.profiles.includes('candidate')) {
                localStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.CANDIDATE_ID, parsedJWT.candidate_id+'');
                route = '/profile/candidate/person';
                analytics.addBreadcrumb('User type: candidate', 'auth', 'info');
            } else {
                localStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.MANAGER_ID, parsedJWT.manager_id+'');

                if (parsedJWT.company_id) {
                   localStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.COMPANY_ID, parsedJWT.company_id);   
                };

                if (parsedJWT.em_unit_id) {
                   localStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.EM_UNIT_ID, parsedJWT.em_unit_id+'');   
                };

                route = '/offers';
                analytics.addBreadcrumb('User type: company', 'auth', 'info');
            };
            
            setTimeout(() => {
                this.setState({ sended: true, loading: false });
                
                // Rastrear login bem-sucedido
                const userType = parsedJWT.profiles.includes('candidate') ? 'candidate' : 'company';
                analytics.trackUserLogin(userType);
                
                analytics.addBreadcrumb(`Redirecting to: ${route}`, 'navigation', 'info');
                
                // Limpar flag de redirecionamento após login bem-sucedido
                sessionStorage.removeItem(AUTH_CONSTANTS.SESSION_STORAGE.REDIRECT_FROM_OFFERS);
                
                if (localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.LEGACY_PASSWORD_LOGIN) === 'true') {
                    route = '/reset-password';
                };

                this.props.navigate(route);
            });
        } catch (error: any) {
            // Rastrear erro de login com contexto específico
            const enhancedError = new Error(`Login failed: ${error.message || 'Unknown error'}`);
            enhancedError.name = error.name || 'LoginError';
            
            analytics.trackError(enhancedError, 'login_failed');
            
            let errorMessage = 'Erro ao fazer login. Tente novamente.';
            let errorTitle = 'Erro no login';
            let errorTextComponent: (() => JSX.Element) | null = null;
            
            // Verificar se é erro de email não validado usando handler reutilizável
            const emailNotValidatedResult = handleEmailNotValidatedError({
                error,
                resendingEmail: this.state.resendingEmail,
                onResend: () => this.handleResendVerificationEmail(),
                defaultMessage: 'Por favor, confirme seu email antes de fazer login.',
                context: 'login',
                allowedStatusCodes: [401, 403]
            });
            
            if (emailNotValidatedResult.isEmailNotValidated) {
                errorTitle = emailNotValidatedResult.errorTitle;
                errorTextComponent = emailNotValidatedResult.errorTextComponent;
            }
            // Verificar se é um erro de Axios
            else if (error.response) {
                analytics.addBreadcrumb(`Login error - HTTP ${error.response.status}`, 'auth', 'error');

                const responseMessageRaw = error.response?.data?.message;
                const responseMessage = typeof responseMessageRaw === 'string'
                    ? responseMessageRaw
                    : Array.isArray(responseMessageRaw)
                        ? responseMessageRaw.join(', ')
                        : null;
                const responseCode = error.response?.data?.code || error.response?.data?.error;
                if (responseCode === 'manager_not_approved' && responseMessage) {
                    errorTitle = 'Aguardando aprovação';
                    errorMessage = responseMessage;
                } else if (responseMessage) {
                    errorMessage = responseMessage;
                } else if (error.response.status === 401) {
                    errorMessage = 'Email ou senha incorretos. Verifique suas credenciais.';
                } else if (error.response.status === 400) {
                    errorMessage = 'Dados inválidos. Verifique o formato do email e senha.';
                } else if (error.response.status >= 500) {
                    errorMessage = 'Erro interno do servidor. Tente novamente em alguns minutos.';
                } else {
                    errorMessage = `Erro do servidor (${error.response.status}). Tente novamente.`;
                }
            } else if (error.request) {
                analytics.addBreadcrumb('Login error - network failure', 'auth', 'error');
                errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
            } else if (error.message) {
                analytics.addBreadcrumb('Login error - unexpected', 'auth', 'error');
                errorMessage = error.message;
            }
            
            const responseData: ResponseData = {
                type: 'error',
                title: errorTitle,
                text: errorTextComponent || (() => <p>{errorMessage}</p>)
            };
            
            // Breadcrumb: Popup de erro sendo exibido
            analytics.addBreadcrumb(
                `Error popup shown: ${errorTitle}`, 
                'ui', 
                'info'
            );
        
            this.setState({ loading: false, responseData });
        }
    }

    onCloseOffersToast = () => {
        this.setState({ showOffersToast: false });
        // Limpar flag quando o toast for fechado
        sessionStorage.removeItem(AUTH_CONSTANTS.SESSION_STORAGE.REDIRECT_FROM_OFFERS);
    }

    render() {
        return (
            <div className='login unlogged-page'>
                <div className='content'>
                    <div className='block'>
                        {window.innerWidth > 1000 &&
                        <div className='img-container'>
                            <img src={loginImg} alt='login-img' />
                        </div>}
                        <div className='block-content'>
                            <div className='text'>
                                <h1>Bem vindo de volta!</h1>
                                <div>
                                    <p>Entre para continuar no</p>
                                    <img className='logo' src={logoImg} alt='logo' />
                                </div>
                            </div>
                            <div className='form' onKeyDown={this.handleKeyDown}>
                                <Input
                                name='username'
                                label='Email'
                                placeholder='Insira seu email'
                                onChange={e => this.setState({ form: { ...this.state.form, username: e }, formErrors: { ...this.state.formErrors, username: [] } })}/>
                                <ErrorMessage errors={this.state.formErrors['username']} />
                                <Input
                                name='password'
                                label='Senha'
                                type='password'
                                placeholder='Insira sua senha'
                                onChange={e => this.setState({ form: { ...this.state.form, password: e }, formErrors: { ...this.state.formErrors, password: [] } })}/>
                                <ErrorMessage errors={this.state.formErrors['password']} />
                                <div className='extra-questions'>
                                    <div className='remember-me' onClick={() => this.setState({ form: { ...this.state.form, remember_me: !this.state.form.remember_me } })}>
                                        <input 
                                            type='checkbox' 
                                            checked={this.state.form.remember_me} 
                                            onChange={() => this.setState({ form: { ...this.state.form, remember_me: !this.state.form.remember_me } })}
                                            readOnly />
                                        <p>Mantenha-me conectado</p>
                                    </div>
                                    <Link to='/reset-password'><p>Esqueceu a senha?</p></Link>
                                </div>
                            </div>
                            <SubmitBtn className='secondary' text='Confirmar' loadingText='Enviando...' loading={this.state.loading} onSubmit={this.onSubmit} />
                            <div className='bottom-text'>
                                <p>Ainda não possui uma conta?</p>
                                <Link to='/sign-up'><b>Cadastrar-se</b></Link>
                            </div>
                        </div>
                    </div>
                </div>
                {this.state.responseData && (
                    <ResponseModal
                    render={true}
                    type={this.state.responseData.type}
                    title={this.state.responseData.title}
                    text={this.state.responseData.text}
                    onClose={() => this.setState({ responseData: null })} />
                )}
                
                <Toast
                show={this.state.showOffersToast}
                message="É necessário fazer login para ver vagas e se candidatar"
                type="info"
                onClose={this.onCloseOffersToast}
                autoClose={true}
                duration={5000} />
            </div>
        )
    }
}

export default withRouter(Login);